// DesignVsShopDrawing.js — ZT Design vs. Shop Drawing Tab (v2 with vault picker)
// Verifies contractor's shop drawings faithfully implement engineer's design intent.
// v2: Added Drawing Vault picker on the design side — design drawings often get
// reused across multiple shop drawing reviews on the same project.

import React, { useState, useRef, useEffect } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD     = '#D4AF37';
const BLUE     = '#0EA5E9';
const BLACK    = '#000000';
const WHITE    = '#FFFFFF';
const LIGHT_BG = '#fffbeb';
const BLUE_BG  = '#f0f9ff';
const BORDER   = '#e5e7eb';

let pdfjsReady;
function ensurePdfjs(version = '3.11.174') {
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
      return resolve(window.pdfjsLib);
    }
    const s = document.createElement('script');
    s.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.min.js`;
    s.onload = () => {
      if (!window.pdfjsLib) return reject(new Error('pdfjsLib missing'));
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    s.onerror = () => reject(new Error('Failed to load PDF.js'));
    document.head.appendChild(s);
  });
  return pdfjsReady;
}

const rasterizeDrawing = async (file) => {
  try {
    if (file.type?.startsWith('image/')) return URL.createObjectURL(file);
    if (!file.type?.includes('pdf')) return null;
    const lib = await ensurePdfjs();
    const buf = await file.arrayBuffer();
    const pdf = await lib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 2.0 });
    const canvas = document.createElement('canvas');
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    return canvas.toDataURL('image/png');
  } catch { return null; }
};

const impactColor = (i) =>
  i === 'High' ? '#dc2626' : i === 'Medium' ? '#d97706' : '#16a34a';

const impactBg = (i) =>
  i === 'High' ? 'rgba(220,38,38,0.15)' : i === 'Medium' ? 'rgba(217,119,6,0.15)' : 'rgba(22,163,74,0.15)';

const MarkupView = ({ drawingFile, findings = [], newDrawingName }) => {
  const [imageUrl, setImageUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [activePin, setActivePin] = React.useState(null);
  const [imgDims, setImgDims] = React.useState({ w: 0, h: 0 });
  const imgRef = React.useRef();

  React.useEffect(() => {
    if (!drawingFile) return;
    setLoading(true);
    rasterizeDrawing(drawingFile).then(url => { setImageUrl(url); setLoading(false); });
  }, [drawingFile]);

  const onImgLoad = () => {
    if (imgRef.current) setImgDims({ w: imgRef.current.offsetWidth, h: imgRef.current.offsetHeight });
  };

  React.useEffect(() => {
    const obs = new ResizeObserver(() => {
      if (imgRef.current) setImgDims({ w: imgRef.current.offsetWidth, h: imgRef.current.offsetHeight });
    });
    if (imgRef.current) obs.observe(imgRef.current);
    return () => obs.disconnect();
  }, [imageUrl]);

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#6b7280' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>🖊️</div>
      <div style={{ fontSize: '14px', fontWeight: '600' }}>Rendering drawing for markup view...</div>
    </div>
  );

  if (!imageUrl) return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
      Unable to render drawing. Markup view supports PDF and image files.
    </div>
  );

  if (findings.length === 0) return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
      No bounding box data available. Re-run the analysis to generate markup positions.
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: '16px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', fontWeight: '600', color: '#374151' }}>Severity:</span>
        {[['High', '#dc2626'], ['Medium', '#d97706'], ['Low', '#16a34a']].map(([label, color]) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: '#6b7280' }}>
            <div style={{ width: '12px', height: '12px', borderRadius: '2px', background: color, opacity: 0.8 }} />
            {label === 'High' ? 'Red Flag' : label === 'Medium' ? 'Review' : 'Info'}
          </div>
        ))}
        <span style={{ fontSize: '12px', color: '#9ca3af', marginLeft: 'auto' }}>
          {findings.length} markup{findings.length !== 1 ? 's' : ''} — click any box to see details
        </span>
      </div>

      <div style={{ position: 'relative', display: 'inline-block', width: '100%', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', background: '#f8fafc' }}>
        <img ref={imgRef} src={imageUrl} alt={newDrawingName} onLoad={onImgLoad} style={{ width: '100%', display: 'block' }} />
        {imgDims.w > 0 && findings.map((f, i) => {
          const bb = f.boundingBox;
          const left = bb.x * imgDims.w;
          const top = bb.y * imgDims.h;
          const width = bb.width * imgDims.w;
          const height = bb.height * imgDims.h;
          const color = impactColor(f.impact);
          const isActive = activePin === i;
          return (
            <React.Fragment key={i}>
              <div onClick={() => setActivePin(isActive ? null : i)} style={{ position: 'absolute', left, top, width, height, border: `2px solid ${color}`, background: isActive ? impactBg(f.impact) : 'rgba(0,0,0,0.04)', borderRadius: '3px', cursor: 'pointer', transition: 'background 0.15s', boxSizing: 'border-box', zIndex: isActive ? 20 : 10 }} />
              <div onClick={() => setActivePin(isActive ? null : i)} style={{ position: 'absolute', left: left - 1, top: Math.max(0, top - 18), background: color, color: '#fff', fontSize: '9px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', cursor: 'pointer', zIndex: 30, whiteSpace: 'nowrap' }}>
                {i + 1} 📐
              </div>
              {isActive && (
                <div style={{ position: 'absolute', left: Math.min(left, imgDims.w - 240), top: top + height + 6, width: '230px', background: '#fff', border: `2px solid ${color}`, borderRadius: '8px', padding: '10px 12px', fontSize: '12px', zIndex: 50, boxShadow: '0 8px 24px rgba(0,0,0,0.18)' }}>
                  <div style={{ fontWeight: '700', color: '#111827', marginBottom: '4px', fontSize: '11px' }}>
                    📐 {f.categoryLabel}
                    <span style={{ float: 'right', color, fontSize: '10px', fontWeight: '600' }}>{f.severity === 'red' ? 'Red Flag' : f.severity === 'yellow' ? 'Review' : 'Info'}</span>
                  </div>
                  <div style={{ color: '#374151', lineHeight: '1.5', marginBottom: '4px' }}>{f.description}</div>
                  {f.designReference && <div style={{ color: '#9ca3af', fontSize: '11px' }}>📋 Design: {f.designReference}</div>}
                  {f.shopReference && <div style={{ color: '#9ca3af', fontSize: '11px' }}>🔧 Shop: {f.shopReference}</div>}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {activePin !== null && findings[activePin] && (
        <div style={{ marginTop: '12px', padding: '12px 16px', background: '#fffbeb', border: '1px solid #D4AF37', borderRadius: '8px', fontSize: '13px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <span style={{ fontWeight: '700', color: '#111827' }}>#{activePin + 1} — {findings[activePin].categoryLabel}</span>
            <span style={{ padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: impactColor(findings[activePin].impact) + '22', color: impactColor(findings[activePin].impact), border: `1px solid ${impactColor(findings[activePin].impact)}44` }}>
              {findings[activePin].severity === 'red' ? 'Red Flag' : findings[activePin].severity === 'yellow' ? 'Review' : 'Info'}
            </span>
            <button onClick={() => setActivePin(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px' }}>×</button>
          </div>
          <div style={{ color: '#374151', lineHeight: '1.6' }}>{findings[activePin].description}</div>
          {findings[activePin].designReference && <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>📋 Design ref: {findings[activePin].designReference}</div>}
          {findings[activePin].shopReference && <div style={{ color: '#6b7280', fontSize: '12px', marginTop: '2px' }}>🔧 Shop ref: {findings[activePin].shopReference}</div>}
          {findings[activePin].recommendedAction && <div style={{ color: '#374151', fontSize: '12px', marginTop: '6px', padding: '6px 10px', background: '#f9fafb', borderRadius: '6px' }}>✅ {findings[activePin].recommendedAction}</div>}
        </div>
      )}
    </div>
  );
};

const generateThumbnail = async (file) => {
  try {
    if (file.type?.startsWith('image/')) return URL.createObjectURL(file);
    if (!file.type?.includes('pdf')) return null;
    const lib = await ensurePdfjs();
    const buf = await file.arrayBuffer();
    const pdf = await lib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 0.5 });
    const canvas = document.createElement('canvas');
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch {
    return null;
  }
};

const TRADES = [
  { value: 'mechanical',      label: 'Mechanical (HVAC, piping)' },
  { value: 'electrical',      label: 'Electrical' },
  { value: 'plumbing',        label: 'Plumbing' },
  { value: 'fire-protection', label: 'Fire Protection' },
  { value: 'structural',      label: 'Structural' },
  { value: 'architectural',   label: 'Architectural' },
  { value: 'other',           label: 'Other' },
];

const SEVERITY_STYLE = {
  red:    { bg: '#fee2e2', fg: '#991b1b', label: '🔴 Red Flag' },
  yellow: { bg: '#fef3c7', fg: '#92400e', label: '🟡 Review' },
  info:   { bg: '#e0f2fe', fg: '#075985', label: '🔵 Info' },
};

const CATEGORY_LABELS = {
  equipmentSubstitution:    { label: 'Equipment Substitution',     icon: '🏭' },
  dimensionalDiscrepancy:   { label: 'Dimensional Discrepancy',    icon: '📏' },
  performanceCapacity:      { label: 'Performance & Capacity',     icon: '⚡' },
  connectionConfiguration:  { label: 'Connection & Configuration', icon: '🔗' },
  codeCompliance:           { label: 'Code & Standards Compliance',icon: '📋' },
  missingDetails:           { label: 'Missing Details',            icon: '🚨' },
  coordinationConflicts:    { label: 'Coordination Conflicts',     icon: '⚠️' },
  specAlignment:            { label: 'Spec Alignment',             icon: '📑' },
};

const RECOMMENDATION_STYLE = {
  noExceptionTaken:     { color: '#16a34a', bg: '#f0fdf4', label: 'No Exception Taken' },
  makeCorrectionsNoted: { color: '#d97706', bg: '#fffbeb', label: 'Make Corrections Noted' },
  reviseAndResubmit:    { color: '#dc2626', bg: '#fef2f2', label: 'Revise and Resubmit' },
  rejected:             { color: '#991b1b', bg: '#fef2f2', label: 'Rejected' },
};

// ── File Preview Modal ──────────────────────────────────────────────────────
const FilePreviewModal = ({ file, thumbnail, isVisible, position, onClose }) => {
  if (!isVisible || !file) return null;
  const w = 400, maxH = 580, pad = 20;
  const left = Math.min(Math.max(position.x + 15, pad), window.innerWidth - w - pad);
  const top  = Math.min(Math.max(position.y - 100, pad), window.innerHeight - maxH - pad);
  const sizeMB = (file.size / 1024 / 1024).toFixed(2);
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', left, top, zIndex: 2000,
        width: w, maxHeight: maxH,
        background: WHITE, border: `3px solid ${GOLD}`,
        borderRadius: '16px', padding: '24px',
        boxShadow: '0 20px 48px rgba(0,0,0,0.4)',
        overflowY: 'auto'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ fontSize: '17px', fontWeight: '700', color: '#111827', margin: 0 }}>📐 Drawing Preview</h3>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6b7280' }}>×</button>
      </div>
      {thumbnail && (
        <div style={{
          display: 'flex', justifyContent: 'center', marginBottom: '16px',
          padding: '10px', background: '#f8fafc', borderRadius: '10px',
          border: '1px solid #e2e8f0'
        }}>
          <img src={thumbnail} alt="preview" style={{
            maxWidth: '320px', maxHeight: '440px', objectFit: 'contain',
            border: '1px solid #d1d5db', borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', background: WHITE
          }} />
        </div>
      )}
      <div style={{
        fontSize: '13px', lineHeight: '1.6',
        background: '#f8fafc', padding: '12px',
        borderRadius: '8px', border: '1px solid #e2e8f0'
      }}>
        <div style={{ fontWeight: '700', marginBottom: '8px', wordBreak: 'break-word', color: '#111827' }}>
          📄 {file.name}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
          <div><strong>Size:</strong> {sizeMB} MB</div>
          <div><strong>Type:</strong> {file.type || 'Unknown'}</div>
        </div>
      </div>
      <div style={{ marginTop: '12px', fontSize: '11px', color: '#6b7280', textAlign: 'center', fontStyle: 'italic' }}>
        Click anywhere to close
      </div>
    </div>
  );
};

// ── Confirm modal for vault delete ──────────────────────────────────────────
const ConfirmModal = ({ isOpen, title, message, confirmLabel, onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div onClick={onCancel} style={{
      position: 'fixed', inset: 0, zIndex: 3000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(2px)'
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: WHITE, borderRadius: '16px', border: `2px solid ${GOLD}`,
        padding: '26px 24px', width: '100%', maxWidth: '380px',
        boxShadow: '0 24px 60px rgba(0,0,0,.28)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <div style={{
            background: '#fef2f2', borderRadius: '50%',
            width: '40px', height: '40px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '18px', flexShrink: 0
          }}>🗑️</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{title}</div>
        </div>
        <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '20px', lineHeight: '1.5', paddingLeft: '52px' }}>
          {message}
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{
            background: 'transparent', border: `1px solid ${BORDER}`,
            borderRadius: '8px', padding: '8px 16px',
            fontSize: '13px', color: '#6b7280', cursor: 'pointer'
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            background: '#dc2626', border: 'none', borderRadius: '8px',
            padding: '8px 16px', fontSize: '13px',
            color: WHITE, cursor: 'pointer', fontWeight: '700'
          }}>{confirmLabel || 'Remove'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Upload section (drop zone + thumb grid) ─────────────────────────────────
const UploadSection = ({
  side, accentColor, accentBg,
  files, onAddFiles, onRemove, onPreview
}) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(null);

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const dropped = [...(e.dataTransfer?.files || e.target?.files || [])];
    if (dropped.length > 0) onAddFiles(dropped);
  };

  return (
    <>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        onClick={() => ref.current?.click()}
        style={{
          border: `2px dashed ${drag ? accentColor : '#d1d5db'}`,
          borderRadius: '10px',
          background: drag ? accentBg : '#f9fafb',
          padding: '14px', textAlign: 'center', cursor: 'pointer',
          minHeight: files.length === 0 ? '110px' : '70px',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          transition: 'all .15s'
        }}
      >
        <input
          ref={ref}
          type="file"
          accept=".pdf,image/*"
          multiple
          style={{ display: 'none' }}
          onChange={handleDrop}
        />
        <div style={{ fontSize: files.length === 0 ? '26px' : '20px', marginBottom: '4px' }}>📐</div>
        <div style={{ fontWeight: '600', fontSize: '13px', color: '#374151', marginBottom: '2px' }}>
          {drag ? 'Drop files here'
            : files.length === 0
              ? `Upload ${side === 'design' ? 'Design' : 'Shop'} Drawing(s)`
              : '+ Add More'}
        </div>
        <div style={{ fontSize: '11px', color: '#9ca3af' }}>
          PDF or image — single or multiple sheets, up to 20 files
        </div>
      </div>

      {files.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(82px, 1fr))',
          gap: '8px',
          marginTop: '12px'
        }}>
          {files.map((f, i) => (
            <div
              key={`${f.file.name}-${i}`}
              style={{
                position: 'relative',
                background: '#f9fafb', borderRadius: '8px',
                border: `2px solid ${hoverIdx === i ? accentColor : BORDER}`,
                padding: '6px', textAlign: 'center',
                transition: 'border-color .15s'
              }}
            >
              <button
                onClick={(e) => { e.stopPropagation(); onRemove(i); }}
                style={{
                  position: 'absolute', top: '3px', right: '3px',
                  background: '#dc2626', color: '#fff',
                  border: 'none', borderRadius: '50%',
                  width: '15px', height: '15px',
                  fontSize: '9px', cursor: 'pointer',
                  lineHeight: '15px', padding: 0, zIndex: 2
                }}
              >×</button>

              <div
                style={{ cursor: f.thumb ? 'pointer' : 'default' }}
                onMouseEnter={(e) => {
                  setHoverIdx(i);
                  if (f.thumb) onPreview(f.file, f.thumb, { x: e.clientX, y: e.clientY });
                }}
                onMouseLeave={() => {
                  setHoverIdx(null);
                  onPreview(null, null, { x: 0, y: 0 });
                }}
              >
                {f.thumb ? (
                  <img
                    src={f.thumb}
                    alt=""
                    style={{
                      width: '52px', height: '66px', objectFit: 'cover',
                      borderRadius: '3px', border: `1px solid ${BORDER}`,
                      transform: hoverIdx === i ? 'scale(1.06)' : 'scale(1)',
                      transition: 'transform .15s'
                    }}
                  />
                ) : (
                  <div style={{ fontSize: '26px', lineHeight: 1, marginBottom: '4px' }}>📄</div>
                )}
              </div>

              <div style={{
                fontSize: '9px', marginTop: '3px',
                wordBreak: 'break-word', fontWeight: '600',
                color: '#374151', lineHeight: '1.3'
              }}>
                {f.file.name.length > 14 ? f.file.name.slice(0, 11) + '…' : f.file.name}
              </div>
              <div style={{ fontSize: '9px', color: '#9ca3af' }}>
                {(f.file.size / 1024 / 1024).toFixed(1)}MB
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

// ── Vault picker (design side only) ─────────────────────────────────────────
const VaultPicker = ({ vault, vaultLoading, selectedIds, onToggle, onDelete }) => {
  if (vaultLoading) {
    return <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280', fontSize: '13px' }}>Loading vault...</div>;
  }
  if (vault.length === 0) {
    return (
      <div style={{
        textAlign: 'center', padding: '24px',
        color: '#6b7280', background: '#f9fafb',
        borderRadius: '8px', fontSize: '13px'
      }}>
        No saved design baselines yet.
        <br />
        <span style={{ fontSize: '11px' }}>
          Check "Save design drawings to Drawing Vault" on your next review to start building your library.
        </span>
      </div>
    );
  }
  return (
    <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
      {vault.map(v => {
        const sel = selectedIds.includes(v.id);
        return (
          <div
            key={v.id}
            onClick={() => onToggle(v.id)}
            style={{
              border: `2px solid ${sel ? GOLD : BORDER}`,
              borderRadius: '8px',
              padding: '10px 14px',
              cursor: 'pointer',
              background: sel ? LIGHT_BG : WHITE,
              marginBottom: '6px',
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all .15s'
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '600', fontSize: '12px', wordBreak: 'break-word' }}>
                {v.fileName}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                {v.projectName && `${v.projectName} · `}
                {v.revisionNumber === 'Design Baseline' && (
                  <span style={{
                    background: GOLD + '22', color: '#92400e',
                    padding: '1px 6px', borderRadius: '8px',
                    fontSize: '10px', fontWeight: '600'
                  }}>Design Baseline</span>
                )}
                {' '}{v.daysLeft}d left
              </div>
            </div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 }}>
              {sel && <span style={{ color: GOLD, fontWeight: '700', fontSize: '14px' }}>✓</span>}
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(v.id); }}
                style={{
                  background: 'transparent', border: `1px solid ${BORDER}`,
                  borderRadius: '6px', padding: '3px 8px',
                  fontSize: '11px', color: '#dc2626', cursor: 'pointer'
                }}
              >×</button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Main Component ──────────────────────────────────────────────────────────
export default function DesignVsShopDrawing({
  user,
  externalDesignFile,
  externalShopFile,
  onExternalDesignFileConsumed,
  onExternalShopFileConsumed,
  getFlag
}) {
  const userId = user?.email || user?.sub || 'anonymous';

  const [designFiles, setDesignFiles] = useState([]);
  const [shopFiles,   setShopFiles]   = useState([]);
  const [designMode,  setDesignMode]  = useState('upload');

  const [vault, setVault] = useState([]);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultLoaded, setVaultLoaded] = useState(false);
  const [selectedVaultIds, setSelectedVaultIds] = useState([]);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, vaultId: null });

  const [projectName, setProjectName] = useState('');
  const [trade, setTrade] = useState('mechanical');
  const [notes, setNotes] = useState('');
  const [saveDesignToVault, setSaveDesignToVault] = useState(false);

  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState(null);
  const [review, setReview] = useState(null);
  const [pdfB64, setPdfB64] = useState(null);
  const [firmLabels, setFirmLabels] = useState(null);
  const [showClear, setShowClear] = useState(false);
  const [expandedCats, setExpandedCats] = useState({});
  const [activeTab, setActiveTab] = useState('findings');
  const [shopDrawingForMarkup, setShopDrawingForMarkup] = useState(null);

  const [previewFile, setPreviewFile] = useState(null);
  const [previewThumb, setPreviewThumb] = useState(null);
  const [previewPos, setPreviewPos] = useState({ x: 0, y: 0 });
  const [previewVisible, setPreviewVisible] = useState(false);

  const handlePreview = (file, thumb, pos) => {
    if (file && thumb) {
      setPreviewFile(file);
      setPreviewThumb(thumb);
      setPreviewPos(pos);
      setPreviewVisible(true);
    } else {
      setPreviewVisible(false);
    }
  };

  const filterAccepted = (files) => {
    const accepted = files.filter(f => {
      const ext = f.name.toLowerCase().split('.').pop();
      return ['pdf', 'jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext);
    });
    if (accepted.length !== files.length) {
      alert('Some files were skipped. Use PDF, JPEG, PNG, WEBP, or GIF.');
    }
    return accepted;
  };

  const addDesignFiles = async (rawFiles) => {
    const accepted = filterAccepted(rawFiles);
    const entries = await Promise.all(accepted.map(async f => ({
      file: f, thumb: await generateThumbnail(f)
    })));
    setDesignFiles(prev => [...prev, ...entries].slice(0, 20));
  };

  const addShopFiles = async (rawFiles) => {
    const accepted = filterAccepted(rawFiles);
    const entries = await Promise.all(accepted.map(async f => ({
      file: f, thumb: await generateThumbnail(f)
    })));
    setShopFiles(prev => [...prev, ...entries].slice(0, 20));
  };

  const removeDesign = (idx) => {
    if (idx === 'all') return setDesignFiles([]);
    setDesignFiles(prev => prev.filter((_, i) => i !== idx));
  };
  const removeShop = (idx) => {
    if (idx === 'all') return setShopFiles([]);
    setShopFiles(prev => prev.filter((_, i) => i !== idx));
  };

  useEffect(() => {
    if (externalDesignFile) {
      addDesignFiles([externalDesignFile]);
      onExternalDesignFileConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalDesignFile]);

  useEffect(() => {
    if (externalShopFile) {
      addShopFiles([externalShopFile]);
      onExternalShopFileConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalShopFile]);

  const loadVault = async (force = false) => {
    if (vaultLoaded && !force) return;
    setVaultLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/drawing-compare/vault/list?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (data.success) {
        const sorted = [...(data.drawings || [])].sort((a, b) => {
          if (a.revisionNumber === 'Design Baseline' && b.revisionNumber !== 'Design Baseline') return -1;
          if (b.revisionNumber === 'Design Baseline' && a.revisionNumber !== 'Design Baseline') return 1;
          return new Date(b.savedAt) - new Date(a.savedAt);
        });
        setVault(sorted);
        setVaultLoaded(true);
      }
    } catch (e) {
      console.error('[DesignVsShop] Vault load error:', e);
    } finally {
      setVaultLoading(false);
    }
  };

  const toggleVaultId = (id) => {
    setSelectedVaultIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);
  };

  const deleteFromVault = (id) => setConfirmModal({ isOpen: true, vaultId: id });

  const confirmDeleteFromVault = async () => {
    const id = confirmModal.vaultId;
    setConfirmModal({ isOpen: false, vaultId: null });
    try {
      await fetch(`${API_BASE}/api/drawing-compare/vault/${id}?userId=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      setVault(prev => prev.filter(d => d.id !== id));
      setSelectedVaultIds(prev => prev.filter(v => v !== id));
    } catch (e) {
      console.error('[DesignVsShop] Vault delete error:', e);
    }
  };

  const handleDesignModeChange = (mode) => {
    setDesignMode(mode);
    if (mode === 'vault') loadVault();
  };

  const handleAnalyze = async () => {
    const designSourceCount = designMode === 'upload' ? designFiles.length : selectedVaultIds.length;

    if (designSourceCount === 0) {
      setError(designMode === 'vault'
        ? 'Please select at least one design baseline from the vault.'
        : 'Please upload at least one design / contract drawing.');
      return;
    }
    if (shopFiles.length === 0) {
      setError("Please upload at least one shop drawing to review.");
      return;
    }

    setAnalyzing(true);
    setError(null);
    setReview(null);
    setPdfB64(null);
    setExpandedCats({});

    try {
      const fd = new FormData();

      if (designMode === 'upload') {
        designFiles.forEach(d => fd.append('designDrawings', d.file));
      } else {
        fd.append('designBaselineIds', JSON.stringify(selectedVaultIds));
      }

      shopFiles.forEach(s => fd.append('shopDrawings', s.file));
      fd.append('projectName', projectName);
      fd.append('trade', trade);
      fd.append('notes', notes);
      fd.append('userId', userId);
      fd.append('saveDesignToVault', designMode === 'upload' && saveDesignToVault ? 'true' : 'false');

      const res = await fetch(`${API_BASE}/api/design-vs-shop/analyze`, {
        method: 'POST',
        body: fd,
      });

      const raw = await res.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch {
        const hint = res.status === 413 ? 'File too large — exceeds upload size limit.'
                   : res.status === 504 ? 'Server timeout — analysis took too long.'
                   : res.status === 502 ? 'Backend unavailable.'
                   : `Server returned ${res.status} ${res.statusText}.`;
        throw new Error(hint);
      }

if (!res.ok) throw new Error(data.error || `Request failed (HTTP ${res.status})`);
      setReview(data.review);
      setPdfB64(data.pdf);
      setFirmLabels(data.firmLabels || null);
      setShopDrawingForMarkup(shopFiles[0]?.file || null);
      setActiveTab('findings');
      setShowClear(true);

      if (designMode === 'upload' && saveDesignToVault) {
        setVaultLoaded(false);
      }
    } catch (e) {
      console.error('[DesignVsShop] analyze error:', e);
      setError(e.message);
      setShowClear(true);
    } finally {
      setAnalyzing(false);
    }
  };

const handleDownloadPdf = async () => {
    if (!review) return;
    try {
      let drawingImageBase64 = null;
      if (shopDrawingForMarkup) {
        drawingImageBase64 = await rasterizeDrawing(shopDrawingForMarkup);
      }
      const r = await fetch(`${API_BASE}/api/design-vs-shop/export-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          review,
          projectName,
          trade,
          designSummary: review.designSetSummary || '',
          shopSummary: review.shopSetSummary || '',
          firmLabels,
          drawingImageBase64,
        })
      });
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AW-Design-vs-Shop-${projectName || Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('PDF export error:', e);
    }
  };

const handleClearAll = () => {
    setDesignFiles([]);
    setShopFiles([]);
    setSelectedVaultIds([]);
    setDesignMode('upload');
    setProjectName('');
    setTrade('mechanical');
    setNotes('');
    setSaveDesignToVault(false);
    setReview(null);
    setPdfB64(null);
    setFirmLabels(null);
    setError(null);
    setShowClear(false);
    setExpandedCats({});
    setActiveTab('findings');
    setShopDrawingForMarkup(null);
  };

  const designSourceCount = designMode === 'upload' ? designFiles.length : selectedVaultIds.length;

  const S = {
    section: { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '16px 20px', marginBottom: '14px' },
    sectionTitle: { fontSize: '14px', fontWeight: '700', color: BLACK, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
    btnGold: { background: GOLD, color: BLACK, border: 'none', borderRadius: '8px', padding: '13px 36px', fontSize: '15px', fontWeight: '700', cursor: 'pointer' },
    btnGhost: { background: 'transparent', color: '#6b7280', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', fontWeight: '600' },
    btnClear: { background: BLACK, color: GOLD, border: 'none', borderRadius: '8px', padding: '13px 28px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    input: { width: '100%', padding: '10px 12px', border: `1px solid ${BORDER}`, borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
    label: { display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '6px' },
    pill: (c) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: c + '22', color: c, border: `1px solid ${c}44` }),
    catHeader: (exp) => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', background: exp ? LIGHT_BG : '#f9fafb', border: `1px solid ${exp ? GOLD : BORDER}`, borderRadius: exp ? '8px 8px 0 0' : '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }),
    sidePanel: { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column' },
  };

  return (
    <div>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title="Remove from Vault"
        message="This design baseline will be permanently removed from your Drawing Vault. This action cannot be undone."
        confirmLabel="Yes, Remove"
        onConfirm={confirmDeleteFromVault}
        onCancel={() => setConfirmModal({ isOpen: false, vaultId: null })}
      />

      <div style={{ marginBottom: '20px', padding: '18px 20px', background: LIGHT_BG, border: `2px solid ${GOLD}`, borderRadius: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
          <span style={{ fontSize: '22px' }}>📐</span>
          <h3 style={{ fontSize: '17px', fontWeight: '700', color: BLACK, margin: 0 }}>
            Design vs. Shop Drawing Review
          </h3>
        </div>
        <p style={{ color: '#374151', fontSize: '13px', lineHeight: '1.55', margin: 0 }}>
          Verify the contractor's shop drawings faithfully implement your design intent. Upload the
          design / contract drawings on the left and the contractor's shop drawings on the right —
          AECWorks AI will cross-reference both sets and flag every meaningful deviation, with the
          recommendation phrased in standard MEP review terminology.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '14px' }}>
        {/* LEFT — Design side with vault toggle */}
        <div style={S.sidePanel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '8px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: '700', color: BLACK }}>📄 Design / Contract Drawings</span>
                {designMode === 'upload' && designFiles.length > 0 && <span style={S.pill(GOLD)}>{designFiles.length}/20</span>}
                {designMode === 'vault' && selectedVaultIds.length > 0 && <span style={S.pill(GOLD)}>{selectedVaultIds.length} selected</span>}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>What the engineer specified — design intent</div>
            </div>
            {designMode === 'upload' && designFiles.length > 0 && (
              <button onClick={() => removeDesign('all')} style={{ background: 'transparent', color: '#dc2626', border: `1px solid ${BORDER}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}>Clear All</button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            <button
              style={{ ...S.btnGhost, ...(designMode === 'upload' ? { background: GOLD, color: BLACK, border: 'none' } : {}) }}
              onClick={() => handleDesignModeChange('upload')}
            >Upload File(s)</button>
            <button
              style={{ ...S.btnGhost, ...(designMode === 'vault' ? { background: GOLD, color: BLACK, border: 'none' } : {}) }}
              onClick={() => handleDesignModeChange('vault')}
            >🗄 Drawing Vault</button>
          </div>

          {designMode === 'upload' ? (
            <UploadSection
              side="design"
              accentColor={GOLD}
              accentBg={LIGHT_BG}
              files={designFiles}
              onAddFiles={addDesignFiles}
              onRemove={removeDesign}
              onPreview={handlePreview}
            />
          ) : (
            <VaultPicker
              vault={vault}
              vaultLoading={vaultLoading}
              selectedIds={selectedVaultIds}
              onToggle={toggleVaultId}
              onDelete={deleteFromVault}
            />
          )}
        </div>

        {/* RIGHT — Shop side */}
        <div style={S.sidePanel}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '8px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '14px', fontWeight: '700', color: BLACK }}>🔧 Shop Drawings</span>
                {shopFiles.length > 0 && <span style={S.pill(BLUE)}>{shopFiles.length}/20</span>}
              </div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>What the contractor proposes to fabricate</div>
            </div>
            {shopFiles.length > 0 && (
              <button onClick={() => removeShop('all')} style={{ background: 'transparent', color: '#dc2626', border: `1px solid ${BORDER}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', flexShrink: 0 }}>Clear All</button>
            )}
          </div>

          <UploadSection
            side="shop"
            accentColor={BLUE}
            accentBg={BLUE_BG}
            files={shopFiles}
            onAddFiles={addShopFiles}
            onRemove={removeShop}
            onPreview={handlePreview}
          />
        </div>
      </div>

      <FilePreviewModal
        file={previewFile}
        thumbnail={previewThumb}
        isVisible={previewVisible}
        position={previewPos}
        onClose={() => setPreviewVisible(false)}
      />

      <div style={S.section}>
        <div style={S.sectionTitle}>🏷️ Project Details (Optional)</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px', marginBottom: '12px' }}>
          <div>
            <label style={S.label}>Project Name</label>
            <input
              style={S.input}
              value={projectName}
              onChange={e => setProjectName(e.target.value)}
              placeholder="e.g. 1185 6th Ave — Mechanical Submittal"
            />
          </div>
          <div>
            <label style={S.label}>Trade</label>
            <select
              style={{ ...S.input, background: WHITE }}
              value={trade}
              onChange={e => setTrade(e.target.value)}
            >
              {TRADES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={S.label}>Reviewer Notes (Optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Anything specific to verify? (e.g. 'Confirm AHU capacities match design schedule', 'Check seismic bracing on all dampers', 'Verify VFD horsepower ratings')"
            rows={3}
            style={{ ...S.input, resize: 'vertical' }}
          />
        </div>

        {designMode === 'upload' && (
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="dvsSaveBaseline"
              checked={saveDesignToVault}
              onChange={e => setSaveDesignToVault(e.target.checked)}
              style={{ width: '16px', height: '16px', accentColor: GOLD }}
            />
            <label htmlFor="dvsSaveBaseline" style={{ fontSize: '13px', cursor: 'pointer' }}>
              Save design drawings to Drawing Vault as baselines (90 days) — reuse across multiple shop drawing reviews
            </label>
          </div>
        )}
{designMode === 'vault' && (
          <div style={{ marginTop: '14px', padding: '8px 12px', background: LIGHT_BG, borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
            {selectedVaultIds.length === 0
              ? '📋 Click any baseline above to select it for this review'
              : `🗄 Using ${selectedVaultIds.length} design baseline${selectedVaultIds.length === 1 ? '' : 's'} from your vault`}
          </div>
        )}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: '#dc2626', fontSize: '13px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', paddingBottom: '20px' }}>
<button
          onClick={handleAnalyze}
          disabled={analyzing || designSourceCount === 0 || shopFiles.length === 0}
          style={{
            ...S.btnGold,
            opacity: (analyzing || designSourceCount === 0 || shopFiles.length === 0) ? 0.5 : 1,
            cursor: (analyzing || designSourceCount === 0 || shopFiles.length === 0) ? 'not-allowed' : 'pointer'
          }}
        >
          {analyzing
            ? 'Reviewing Submittal...'
            : `Review ${shopFiles.length || ''} Shop Drawing${shopFiles.length === 1 ? '' : 's'} vs. Design`.replace('  ', ' ').trim()}
        </button>
        {showClear && (
          <button onClick={handleClearAll} style={S.btnClear}>🗑️ Clear All</button>
        )}
      </div>

      {analyzing && (
        <div style={{ padding: '20px', background: LIGHT_BG, border: `1px solid ${GOLD}`, borderRadius: '8px', marginBottom: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: BLACK, marginBottom: '8px' }}>
            Comparing {designSourceCount} design drawing{designSourceCount === 1 ? '' : 's'} against {shopFiles.length} shop drawing{shopFiles.length === 1 ? '' : 's'}
          </div>
          <div style={{ background: BORDER, borderRadius: '99px', height: '6px', overflow: 'hidden', maxWidth: '380px', margin: '0 auto 8px', position: 'relative' }}>
            <div style={{
              position: 'absolute', top: 0, left: 0,
              height: '100%', width: '40%',
              background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`,
              borderRadius: '99px',
              animation: 'dvsSlide 1.2s linear infinite'
            }} />
          </div>
          <style>{`@keyframes dvsSlide { 0% { left: -40%; } 100% { left: 100%; } }`}</style>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            Cross-referencing design intent against shop proposals — usually 30-60 seconds
          </div>
        </div>
      )}

      {review && !analyzing && (
        <div>
{review.overallRecommendation && (() => {
            const rec = RECOMMENDATION_STYLE[review.overallRecommendation] || RECOMMENDATION_STYLE.makeCorrectionsNoted;
            // Use firm-customized label if available, fall back to AECWorks default
            const displayLabel = (firmLabels && firmLabels[review.overallRecommendation]) || rec.label;
            return (
              <div style={{ padding: '18px 22px', marginBottom: '14px', background: rec.bg, borderLeft: `6px solid ${rec.color}`, borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '700', color: '#6b7280', marginBottom: '6px', letterSpacing: '.05em' }}>Overall Recommendation</div>
                <div style={{ fontSize: '22px', fontWeight: '800', color: rec.color, marginBottom: '8px' }}>{displayLabel}</div>
                {review.overallRecommendationReason && (
                  <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.5' }}>{review.overallRecommendationReason}</p>
                )}
              </div>
            );
          })()}

          {review.summary && (
            <div style={{ ...S.section, marginBottom: '14px' }}>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', fontWeight: '700', color: '#6b7280', marginBottom: '6px', letterSpacing: '.05em' }}>Executive Summary</div>
              <p style={{ fontSize: '14px', color: '#374151', margin: 0, lineHeight: '1.55' }}>{review.summary}</p>
            </div>
          )}

          {(review.designSetSummary || review.shopSetSummary) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', background: LIGHT_BG, border: `1px solid ${GOLD}`, borderRadius: '8px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', color: '#92400e', marginBottom: '6px', letterSpacing: '.05em' }}>📄 Design Intent</div>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>{review.designSetSummary || '—'}</p>
              </div>
              <div style={{ padding: '14px', background: BLUE_BG, border: `1px solid ${BLUE}`, borderRadius: '8px' }}>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '700', color: '#075985', marginBottom: '6px', letterSpacing: '.05em' }}>🔧 Shop Proposal</div>
                <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>{review.shopSetSummary || '—'}</p>
              </div>
            </div>
          )}

          {/* Tab bar */}
          <div style={{ display: 'flex', marginBottom: '14px', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
            {[['findings', '📋 Findings'], ['markup', '🖊️ Markup View']].map(([id, label]) => (
              <button key={id} onClick={() => setActiveTab(id)} style={{
                flex: 1, padding: '10px 16px', border: 'none',
                borderRight: id === 'findings' ? `1px solid ${BORDER}` : 'none',
                background: activeTab === id ? GOLD : WHITE,
                color: activeTab === id ? BLACK : '#6b7280',
                fontWeight: activeTab === id ? '700' : '400',
                fontSize: '13px', cursor: 'pointer', transition: 'all .15s',
              }}>{label}</button>
            ))}
          </div>

            {activeTab === 'findings' && review.findings && review.findings.length > 0 && (() => {
            const total = review.findings.length;
            const red = review.findings.filter(f => f.severity === 'red').length;
            const yellow = review.findings.filter(f => f.severity === 'yellow').length;
            return (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                {[
                  ['📋', total, 'Total Findings', BLACK],
                  ['🔴', red, 'Red Flags', '#dc2626'],
                  ['🟡', yellow, 'Review Items', '#d97706']
                ].map(([icon, val, lbl, clr]) => (
                  <div key={lbl} style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '10px 16px', textAlign: 'center', flex: '1 1 110px', minWidth: '110px' }}>
                    <div style={{ fontSize: '15px', marginBottom: '2px' }}>{icon}</div>
                    <div style={{ fontSize: '20px', fontWeight: '800', color: clr }}>{val}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{lbl}</div>
                  </div>
                ))}
              </div>
            );
          })()}

          {(() => {
            const grouped = (review.findings || []).reduce((acc, f) => {
              (acc[f.category] = acc[f.category] || []).push(f);
              return acc;
            }, {});
            return Object.entries(CATEGORY_LABELS).map(([key, { label, icon }]) => {
              const items = grouped[key];
              if (!items || items.length === 0) return null;
              const exp = expandedCats[key];
              return (
                <div key={key} style={{ marginBottom: '6px' }}>
                  <div style={S.catHeader(exp)} onClick={() => setExpandedCats(p => ({ ...p, [key]: !p[key] }))}>
                    <span>{icon} {label} <span style={S.pill(GOLD)}>{items.length}</span></span>
                    <span style={{ color: '#9ca3af' }}>{exp ? '▲' : '▼'}</span>
                  </div>
                  {exp && (
                    <div style={{ border: `1px solid ${GOLD}`, borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                      {items.map((f, i) => {
                        const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.info;
                        return (
                          <div key={i} style={{ padding: '14px', background: i % 2 === 0 ? WHITE : '#fafafa', borderBottom: i < items.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                              <span style={{ display: 'inline-block', padding: '3px 10px', background: sev.bg, color: sev.fg, borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>{sev.label}</span>
                              {f.designReference && (
                                <span style={{ fontSize: '11px', color: '#92400e', background: LIGHT_BG, padding: '2px 8px', borderRadius: '10px', border: `1px solid ${GOLD}44` }}>📄 Design: {f.designReference}</span>
                              )}
                              {f.shopReference && (
                                <span style={{ fontSize: '11px', color: '#075985', background: BLUE_BG, padding: '2px 8px', borderRadius: '10px', border: `1px solid ${BLUE}44` }}>🔧 Shop: {f.shopReference}</span>
                              )}
                            </div>
                            <div style={{ fontWeight: '600', fontSize: '14px', color: BLACK, marginBottom: '6px' }}>{f.issue}</div>
                            {f.designIntent && (
                              <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                                <strong style={{ color: '#92400e' }}>Design intent:</strong> {f.designIntent}
                              </div>
                            )}
                            {f.shopProposal && (
                              <div style={{ fontSize: '13px', color: '#374151', marginBottom: '4px' }}>
                                <strong style={{ color: '#075985' }}>Shop proposal:</strong> {f.shopProposal}
                              </div>
                            )}
                            {f.why && (
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>{f.why}</div>
                            )}
                            {f.recommendedAction && (
                              <div style={{ marginTop: '8px', padding: '8px 12px', background: LIGHT_BG, borderRadius: '6px', fontSize: '13px', color: '#374151', fontStyle: 'italic' }}>
                                → {f.recommendedAction}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {activeTab === 'findings' && review.missingFromShop && review.missingFromShop.length > 0 && (
            <div style={{ ...S.section, marginTop: '14px' }}>
              <div style={S.sectionTitle}>
                🚨 Missing from Shop Drawings
                <span style={{ fontSize: '12px', fontWeight: '500', color: '#6b7280' }}>(required by design)</span>
              </div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {review.missingFromShop.map((m, i) => (
                  <li key={i} style={{ fontSize: '13px', color: '#374151', marginBottom: '6px', lineHeight: '1.5' }}>{m}</li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === 'findings' && review.reviewerNotes && (
            <div style={{ padding: '14px 18px', marginTop: '14px', background: LIGHT_BG, border: `1px solid ${GOLD}`, borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', marginBottom: '6px', letterSpacing: '.05em' }}>EOR Should Double-Check</div>
              <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.5' }}>{review.reviewerNotes}</p>
            </div>
          )}

          {pdfB64 && (
            <div style={{ textAlign: 'center', marginTop: '20px', paddingBottom: '8px' }}>
              {activeTab === 'markup' && (
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px 20px', marginBottom: '14px' }}>
              <MarkupView
                drawingFile={shopDrawingForMarkup}
                findings={(review.findings || []).filter(f => f.boundingBox).map((f, i) => ({
                  ...f,
                  impact: f.severity === 'red' ? 'High' : f.severity === 'yellow' ? 'Medium' : 'Low',
                  description: f.issue,
                  categoryLabel: f.category,
                  categoryIcon: '📐',
                  globalIdx: i,
                }))}
                newDrawingName={shopFiles[0]?.name || 'Shop Drawing'}
              />
            </div>
          )}
              <button onClick={handleDownloadPdf} style={{ ...S.btnGold, padding: '11px 28px', fontSize: '14px' }}>
                Download PDF Report
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}